import { useEffect, useMemo, useState } from 'react';
import { db } from '@/lib/db';
import type { CallYourShot, Course, Member, MemberPublic } from '@/lib/db/types';
import { getCourseCampus, getCourseComboLabel } from '@/lib/courses/group';
import { Sheet } from '@/components/shared/Sheet';
import { Avatar } from '@/components/shared/Avatar';
import { Crest } from '@/components/shared/Crest';
import { EmptyState } from '@/components/shared/EmptyState';
import { ProposeShotSheet } from '@/components/shots/ProposeShotSheet';
import { isValidCallActualScore } from '@/lib/shots/call-shot-settle';

interface Props {
  member: Member;
  onClose: () => void;
}

type Tab = 'live' | 'mine' | 'history';

export function CallYourShotSheet({ member, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('live');
  const [shots, setShots] = useState<CallYourShot[]>([]);
  const [members, setMembers] = useState<MemberPublic[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [openPropose, setOpenPropose] = useState(false);
  const [settleDrafts, setSettleDrafts] = useState<Record<string, number>>({});
  const [settling, setSettling] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const memberById = useMemo(
    () => Object.fromEntries(members.map((m) => [m.id, m])),
    [members],
  );
  const courseById = useMemo(
    () => Object.fromEntries(courses.map((c) => [c.id, c])),
    [courses],
  );

  const load = async () => {
    const data = await db.callShots.list();
    setShots(data);
  };

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const [shotData, memberData, courseData] = await Promise.all([
          db.callShots.list(),
          db.members.list({ limit: 100 }),
          db.courses.list(),
        ]);
        if (!active) return;
        setShots(shotData);
        setMembers(memberData);
        setCourses(courseData);
      } catch (e) {
        if (active) setError((e as Error).message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const refresh = () => { void load(); };
    window.addEventListener('bagtag:refresh-feed', refresh);
    window.addEventListener('bagtag:call-settled', refresh);
    return () => {
      window.removeEventListener('bagtag:refresh-feed', refresh);
      window.removeEventListener('bagtag:call-settled', refresh);
    };
  }, []);

  const liveShots = shots.filter((s) => s.status === 'pending');
  const myShots = shots.filter((s) => s.player_id === member.id);
  const myActive = myShots.filter((s) => s.status === 'pending');
  const myHistory = myShots.filter((s) => s.status === 'completed' || s.status === 'cancelled');
  const allHistory = shots.filter((s) => s.status === 'completed');

  const shown = tab === 'live' ? liveShots : tab === 'mine' ? myActive : allHistory;

  const handlePropose = async (data: {
    course_id: string;
    tee_id: string | null;
    target_score: number;
    notes?: string;
  }) => {
    setError(null);
    try {
      await db.callShots.propose({
        ...data,
        tee_id: data.tee_id ?? undefined,
      });
      setOpenPropose(false);
      await load();
      setTab('mine');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleSettle = async (shotId: string) => {
    const score = settleDrafts[shotId];
    const holesKind = score >= 50 ? 'full' as const : 'front-9' as const;
    if (!isValidCallActualScore(score, holesKind)) return;
    setSettling(shotId);
    try {
      await db.callShots.settle(shotId, score);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSettling(null);
    }
  };

  const handleReact = async (shotId: string, type: 'eyes' | 'fire' | 'target') => {
    try {
      await db.callShots.react(shotId, type);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const myHits = myHistory.filter((s) => s.resolution === 'hit' || s.resolution === 'statement').length;
  const myMisses = myHistory.filter((s) => s.resolution === 'missed').length;
  const myStatements = myHistory.filter((s) => s.resolution === 'statement').length;

  const resolutionBadge = (resolution: CallYourShot['resolution']) => {
    if (resolution === 'statement') return { label: 'STATEMENT', className: 'bg-gold/20 text-gold-deep' };
    if (resolution === 'hit') return { label: 'CALLED IT', className: 'bg-good/15 text-good' };
    if (resolution === 'missed') return { label: 'MISSED', className: 'bg-bad/15 text-bad' };
    if (resolution === 'exact') return { label: 'EXACT', className: 'bg-navy/10 text-navy' };
    return null;
  };

  return (
    <>
      <Sheet title="Call Your Shot" onClose={onClose}>
        <div className="px-4 pb-8 pt-1">
          <div className="relative mb-3.5 flex items-center gap-3 overflow-hidden rounded-[14px] bg-navy p-3.5 text-paper">
            <div className="pointer-events-none absolute -right-4 -top-4 opacity-[0.08]">
              <Crest size={100} variant="dark" />
            </div>
            <div className="relative flex-1">
              <div className="text-[10px] font-bold tracking-[0.15em] text-gold">CALL YOUR NUMBER</div>
              <div className="mt-1 font-serif text-[17px] font-bold leading-tight">
                Set your target. Earn points if you hit it.
              </div>
              <div className="mt-1 text-[11px] text-white/65">
                +10 pts per stroke under your call. -5 per stroke over. Members react — they don&apos;t wager against you.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpenPropose(true)}
              className="relative shrink-0 rounded-[10px] bg-gold px-3.5 py-2.5 text-xs font-bold tracking-wide text-navy"
            >
              + CALL
            </button>
          </div>

          <div className="mb-3 rounded-md border-l-2 border-gold bg-gold/10 px-3 py-2 text-[10.5px] leading-snug text-ink-soft">
            <strong className="text-navy">Self-call only · Points only.</strong>{' '}
            Reactions are recognition, not wagers. No money, no point exchange between members.
          </div>

          {liveShots.length > 0 && (
            <div className="mb-3.5 rounded-[14px] border-[1.5px] border-navy bg-paper p-3 shadow-sm">
              <div className="mb-2.5 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-good shadow-[0_0_8px_theme(colors.good)]" />
                <div className="font-serif text-[13px] font-bold text-navy">Live Calls</div>
                <div className="rounded-full bg-good px-1.5 py-px text-[9px] font-bold tracking-wide text-paper">
                  {liveShots.length} LIVE
                </div>
              </div>
              {liveShots.map((s) => {
                const caller = memberById[s.player_id] ?? { name: 'Member' };
                const course = s.course_id ? courseById[s.course_id] : null;
                const isMine = s.player_id === member.id;
                return (
                  <div
                    key={s.id}
                    className={`mb-1.5 flex items-center gap-2.5 rounded-[10px] border px-3 py-2.5 last:mb-0 ${
                      isMine ? 'border-gold bg-gold/10' : 'border-line bg-cream'
                    }`}
                  >
                    <Avatar member={caller} size={32} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-bold text-ink">
                        {caller.name}
                        {isMine && <span className="font-semibold text-gold-deep"> · your call</span>}
                      </div>
                      {course && (
                        <div className="mt-0.5 truncate text-[10px] text-mute">
                          {getCourseCampus(course)} {getCourseComboLabel(course)}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-[9px] font-bold tracking-wide text-mute">TARGET</div>
                      <div className="font-serif text-lg font-bold leading-none text-navy">{s.target_score}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mb-3 flex gap-1 rounded-[10px] bg-cream p-1">
            {([
              { id: 'live' as const, label: `All Calls${liveShots.length ? ` · ${liveShots.length}` : ''}` },
              { id: 'mine' as const, label: `Mine${myActive.length ? ` · ${myActive.length}` : ''}` },
              { id: 'history' as const, label: 'History' },
            ]).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex-1 rounded-lg px-1 py-2 text-xs font-bold ${
                  tab === t.id ? 'bg-paper text-navy shadow-sm' : 'text-ink-soft'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {error && <div className="mb-3 rounded-xl bg-bad/10 p-3 text-sm text-bad">{error}</div>}
          {loading && <div className="text-sm text-mute">Loading…</div>}

          {tab === 'history' && myHistory.length > 0 && (
            <div className="mb-3 flex justify-around rounded-xl bg-paper p-3.5 shadow-sm">
              <div className="text-center">
                <div className="text-[10px] font-bold tracking-wide text-mute">RECORD</div>
                <div className="mt-0.5 font-serif text-[22px] font-bold text-navy">{myHits}-{myMisses}</div>
              </div>
              <div className="border-x border-line px-5 text-center">
                <div className="text-[10px] font-bold tracking-wide text-mute">STATEMENTS</div>
                <div className="mt-0.5 font-serif text-[22px] font-bold text-gold">{myStatements}</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] font-bold tracking-wide text-mute">HIT RATE</div>
                <div className="mt-0.5 font-serif text-[22px] font-bold text-good">
                  {myHistory.length ? Math.round((myHits / myHistory.length) * 100) : 0}%
                </div>
              </div>
            </div>
          )}

          {!loading && shown.length === 0 && (
            <EmptyState
              text={
                tab === 'live'
                  ? 'No live calls right now. Be the first to call your shot.'
                  : tab === 'mine'
                    ? "You haven't called any shots. Tap + CALL to set your number."
                    : 'No completed calls yet.'
              }
            />
          )}

          <div className="space-y-2">
            {shown.map((shot) => {
              const isMine = shot.player_id === member.id;
              const caller = memberById[shot.player_id] ?? { name: 'Member' };
              const course = shot.course_id ? courseById[shot.course_id] : null;
              const badge = shot.status === 'completed' ? resolutionBadge(shot.resolution) : null;
              const historical = tab === 'history';

              return (
                <div
                  key={shot.id}
                  className={`rounded-[14px] border bg-paper p-3.5 shadow-sm ${
                    isMine && !historical ? 'border-gold' : 'border-line'
                  }`}
                >
                  <div className="mb-2.5 flex items-center gap-2.5">
                    <Avatar member={caller} size={40} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold tracking-wide text-mute">CALLED BY</div>
                      <div className="font-serif text-[15px] font-bold text-ink">
                        {caller.name}
                        {isMine && <span className="text-gold-deep"> · You</span>}
                      </div>
                      {course && (
                        <div className="mt-0.5 text-[10px] text-mute">
                          {getCourseCampus(course)} · {getCourseComboLabel(course)} · Par {course.par}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold tracking-wide text-mute">TARGET</div>
                      <div className="font-serif text-[26px] font-bold leading-none text-navy">{shot.target_score}</div>
                    </div>
                  </div>

                  {shot.notes && (
                    <div className="mb-2.5 rounded-lg bg-cream px-2.5 py-2 text-xs italic leading-snug text-ink-soft">
                      &ldquo;{shot.notes}&rdquo;
                    </div>
                  )}

                  {badge && (
                    <div className={`mb-2 inline-block rounded-md px-2 py-0.5 text-[10px] font-bold tracking-wide ${badge.className}`}>
                      {badge.label}
                    </div>
                  )}

                  {shot.status === 'pending' && !isMine && (
                    <div className="flex gap-2">
                      {(['eyes', 'fire', 'target'] as const).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => void handleReact(shot.id, type)}
                          className="rounded-full border border-line px-3 py-1 text-xs font-semibold"
                        >
                          {type === 'eyes' ? '👀' : type === 'fire' ? '🔥' : '🎯'}
                        </button>
                      ))}
                    </div>
                  )}

                  {shot.status === 'pending' && isMine && (
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min={25}
                        max={150}
                        placeholder="Actual gross"
                        value={settleDrafts[shot.id] ?? ''}
                        onChange={(e) => setSettleDrafts((p) => ({ ...p, [shot.id]: Number(e.target.value) }))}
                        className="flex-1 rounded-[10px] border border-line bg-paper px-3 py-2 text-sm outline-none"
                      />
                      <button
                        type="button"
                        disabled={settling === shot.id}
                        onClick={() => void handleSettle(shot.id)}
                        className="rounded-[10px] bg-navy px-4 py-2 text-sm font-semibold text-paper disabled:opacity-50"
                      >
                        {settling === shot.id ? '…' : 'Settle'}
                      </button>
                    </div>
                  )}

                  {shot.status === 'completed' && shot.actual_score != null && (
                    <div className="mt-2 rounded-lg bg-cream px-2.5 py-2 text-sm text-ink-soft">
                      Shot {shot.actual_score}
                      {shot.points_awarded != null && (
                        <span className={shot.points_awarded >= 0 ? ' text-good' : ' text-bad'}>
                          {' '}· {shot.points_awarded > 0 ? '+' : ''}{shot.points_awarded} pts
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Sheet>

      {openPropose && (
        <ProposeShotSheet
          member={member}
          onClose={() => setOpenPropose(false)}
          onSubmit={(data) => void handlePropose(data)}
        />
      )}
    </>
  );
}
